import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { HUDDLE_SIGNAL, WS_SEND_MESSAGE } from '@common/constants/webSockets'
import { IHuddle, IHuddleInvite } from '@common/interfaces/huddles'
import api from '../api'
import { SnackbarType } from '../components/Common/Snackbar'
import { createLiveAudioContext } from '../helpers/audio/levels'
import { openMicrophone } from '../helpers/audio/microphone'
import { HuddleAudio } from '../helpers/huddle/huddleAudio'
import { huddleDevices } from '../helpers/huddle/huddleDevices'
import { HuddleMesh } from '../helpers/huddle/huddleMesh'
import { HuddleRecorder } from '../helpers/huddle/huddleRecorder'
import {
  HuddleSession,
  HuddleSessionStatus,
  IHuddleSessionDeps,
  IHuddleSnapshot,
} from '../helpers/huddle/huddleSession'
import { huddleStore } from '../helpers/huddle/huddleStore'
import useArcInHuddle from '../hooks/useArcInHuddle'
import useHuddleInvites from '../hooks/useHuddleInvites'
import { canCaptureAudio, pickContainer, VoiceRecordingError } from '../hooks/useVoiceRecording'
import { useDispatch } from '../redux/hooks'
import { putNotification } from '../redux/ui/actions'

const MIC_ERROR_MESSAGES: Record<VoiceRecordingError, string> = {
  [VoiceRecordingError.PermissionDenied]:
    'Microphone access is blocked. Allow it in your browser settings to join a huddle.',
  [VoiceRecordingError.NoMicrophone]: 'No microphone found. Connect one to join a huddle.',
  [VoiceRecordingError.Unsupported]: 'This browser can’t stream audio. Try Chrome or Safari.',
  [VoiceRecordingError.Failed]: 'Couldn’t start the microphone. Please try again.',
}

const getMicrophone = (): Promise<MediaStream> =>
  openMicrophone({ deviceId: huddleDevices.get().inputId, messages: MIC_ERROR_MESSAGES })

const createRecorder: IHuddleSessionDeps['createRecorder'] = (recorder) => {
  const container = pickContainer()
  return container ? new HuddleRecorder({ ...recorder, container }) : null
}

const createAudio = (mic: MediaStream, context: AudioContext): HuddleAudio => {
  const audio = new HuddleAudio(mic, context)
  audio.setOutputDevice(huddleDevices.get().outputId)
  return audio
}

/** Anyone not in the conversation yet is added to it, then everyone is rung. */
const inviteAnyone = async (session: HuddleSession, userIds: string[]): Promise<void> => {
  const { channelId } = session.getSnapshot()
  const members = (await api.channels.listMembers(channelId)) as { userId: string }[]
  const memberIds = new Set(members.map(({ userId }) => userId))
  const newcomers = userIds.filter((userId) => !memberIds.has(userId))
  if (newcomers.length) await api.channels.addMembers(channelId, newcomers)
  await session.invite(userIds)
}

interface IHuddleContext {
  session: HuddleSession | null
  /** Null while this tab is in no huddle. */
  snapshot: IHuddleSnapshot | null
  /** Start the conversation's huddle, or join the one going there. Call inside the click. */
  start: (channelId: string) => void
  /** Join one huddle, from its row in the conversation. */
  join: (channelId: string, huddleId: string) => void
  leave: () => void
  toggleMute: () => void
  /** Arc is in this tab's huddle, hosted from this browser. */
  isHostingArc: boolean
  inviteArc: () => void
  dismissArc: () => void
  /** Talk through this mic in huddles from now on; null is the system default. */
  chooseMicrophone: (inputId: string | null) => void
  /** Hear huddles through this output from now on; null is the system default. */
  chooseSpeaker: (outputId: string | null) => void
  /** Ring members of the conversation to join this tab's huddle. */
  invite: (userIds: string[]) => void
  /** Someone asked you into their huddle; null once answered, dismissed or stale. */
  pendingInvite: IHuddleInvite | null
  dismissInvite: () => void
}

const HuddleContext = createContext<IHuddleContext | null>(null)

const subscribeNothing = () => () => {}
const getNoSnapshot = (): IHuddleSnapshot | null => null

export const HuddleProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch()
  const [session, setSession] = useState<HuddleSession | null>(null)
  const sessionRef = useRef<HuddleSession | null>(null)
  const snapshot = useSyncExternalStore(
    session?.subscribe ?? subscribeNothing,
    session?.getSnapshot ?? getNoSnapshot,
  )

  const notify = useCallback(
    (message: string, isError: boolean) => {
      dispatch(putNotification({ message, type: isError ? SnackbarType.Error : SnackbarType.Info }))
    },
    [dispatch],
  )

  const deps = useMemo<IHuddleSessionDeps>(
    () => ({
      api: api.channels,
      getMicrophone,
      createAudioContext: createLiveAudioContext,
      createAudio,
      createRecorder,
      createMesh: (meshDeps) => new HuddleMesh(meshDeps),
      sendSignal: (signal) => {
        dispatch({ type: WS_SEND_MESSAGE, payload: { type: HUDDLE_SIGNAL, data: signal } })
      },
      onSignal: (listener) => huddleStore.onSignal(listener),
      onHuddle: (listener) => huddleStore.onHuddle(listener),
      onSpeakerCheck: (listener) => huddleStore.onSpeakerCheck(listener),
      publish: (channelId, huddle) => huddleStore.set(channelId, huddle),
      notify,
    }),
    [dispatch, notify],
  )

  const isInHuddle = !!snapshot && snapshot.status !== HuddleSessionStatus.Ended
  const liveHuddleId = isInHuddle ? (snapshot.huddle?.id ?? null) : null
  const { isHostingArc, isOnPrivateArcCall, inviteArc, dismissArc } = useArcInHuddle({
    sessionRef,
    isInHuddle,
    huddleId: liveHuddleId,
    notify,
  })
  const { pendingInvite, dismissInvite } = useHuddleInvites(sessionRef)

  const open = useCallback(
    (channelId: string, huddleId?: string) => {
      const current = sessionRef.current?.getSnapshot()
      if (current && current.status !== HuddleSessionStatus.Ended) {
        if (current.channelId !== channelId)
          notify('Leave your huddle first to join another.', true)
        return
      }
      if (isOnPrivateArcCall) {
        notify('Hang up Arc Voice first to join a huddle.', true)
        return
      }
      if (!canCaptureAudio()) {
        notify(MIC_ERROR_MESSAGES[VoiceRecordingError.Unsupported], true)
        return
      }
      const next = new HuddleSession(deps, channelId)
      sessionRef.current = next
      setSession(next)
      void next.open(huddleId)
    },
    [deps, isOnPrivateArcCall, notify],
  )

  const start = useCallback((channelId: string) => open(channelId), [open])
  const join = useCallback(
    (channelId: string, huddleId: string) => open(channelId, huddleId),
    [open],
  )
  const leave = useCallback(() => sessionRef.current?.leave(), [])
  const invite = useCallback(
    (userIds: string[]) => {
      const current = sessionRef.current
      if (!current) return
      inviteAnyone(current, userIds)
        .then(() => notify(userIds.length > 1 ? 'Invites sent.' : 'Invite sent.', false))
        .catch((err: Error) => notify(err?.message || 'Couldn’t send the invite.', true))
    },
    [notify],
  )

  const chooseMicrophone = useCallback((inputId: string | null) => {
    huddleDevices.set({ inputId })
    void sessionRef.current?.switchMicrophone()
  }, [])
  const chooseSpeaker = useCallback((outputId: string | null) => {
    huddleDevices.set({ outputId })
    sessionRef.current?.setOutputDevice(outputId)
  }, [])
  const toggleMute = useCallback(() => sessionRef.current?.toggleMute(), [])

  useEffect(() => {
    const handlePageHide = () => sessionRef.current?.handlePageHide()
    window.addEventListener('pagehide', handlePageHide)
    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [])

  const value = useMemo<IHuddleContext>(
    () => ({
      session,
      snapshot: isInHuddle ? snapshot : null,
      start,
      join,
      leave,
      toggleMute,
      isHostingArc,
      inviteArc,
      dismissArc,
      chooseMicrophone,
      chooseSpeaker,
      invite,
      pendingInvite,
      dismissInvite,
    }),
    [
      session,
      isInHuddle,
      snapshot,
      start,
      join,
      leave,
      toggleMute,
      isHostingArc,
      inviteArc,
      dismissArc,
      chooseMicrophone,
      chooseSpeaker,
      invite,
      pendingInvite,
      dismissInvite,
    ],
  )

  return <HuddleContext.Provider value={value}>{children}</HuddleContext.Provider>
}

const NO_HUDDLE: IHuddleContext = {
  session: null,
  snapshot: null,
  start: () => {},
  join: () => {},
  leave: () => {},
  toggleMute: () => {},
  isHostingArc: false,
  inviteArc: () => {},
  dismissArc: () => {},
  chooseMicrophone: () => {},
  chooseSpeaker: () => {},
  invite: () => {},
  pendingInvite: null,
  dismissInvite: () => {},
}

/** The huddle, or a no-op stand-in outside the provider (snapshot tests, the login screens). */
export const useHuddle = (): IHuddleContext => useContext(HuddleContext) ?? NO_HUDDLE

/** The live huddle in a conversation, fetched once and then kept current from the socket. */
export const useChannelHuddle = (channelId: string | null | undefined): IHuddle | null => {
  const subscribe = huddleStore.subscribe
  const huddle = useSyncExternalStore(subscribe, () =>
    channelId ? huddleStore.get(channelId) : null,
  )
  useEffect(() => {
    if (!channelId) return undefined
    let isCurrent = true
    api.channels
      .getHuddle(channelId)
      .then((live) => {
        if (isCurrent) huddleStore.set(channelId, live)
      })
      .catch(() => {})
    return () => {
      isCurrent = false
    }
  }, [channelId])
  return huddle ?? null
}
